import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  LinearProgress
} from '@mui/material';
import {
  Add,
  ArrowBack,
  CloudDownload,
  CloudUpload,
  Delete,
  Description,
  Edit,
  ExpandMore,
  History,
  Info,
  Refresh,
  Schedule,
  Visibility,
  Warning,
  CheckCircle,
  Error,
  AccessTime,
  Person,
  CalendarToday,
  FilePresent
} from '@mui/icons-material';
import { ROUTES } from '../../../routes';
import { STAT_FIELDS, DOCUMENT_TYPE_MAPPING } from '../../../Configurations/StatFieldData';
import { API_ENDPOINTS, API_BASE_V1 } from '../../../Api/endpoints';
import { addAuthHeader, getAuthToken } from '../../../utils/authUtils';
import axios from 'axios';

// Document categories from StatFieldData.js
const DOCUMENT_CATEGORIES = [
  ...Object.entries(STAT_FIELDS)
    .filter(([key, value]) => value.type === 'FILE')
    .map(([key, value]) => ({
      value: key,
      label: value.label
    })),
  { value: 'other', label: 'Other' }
];

// Format date for display
const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (includeTime) {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Calculate days until expiry
const calculateDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const expiry = new Date(expiryDate);
  const today = new Date();
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Get expiration status and color
const getExpirationStatus = (expiryDate) => {
  if (!expiryDate) return { status: 'No Expiry', color: 'default', severity: 'info' };
  
  const daysUntilExpiry = calculateDaysUntilExpiry(expiryDate);
  
  if (daysUntilExpiry < 0) {
    return { status: `Expired ${Math.abs(daysUntilExpiry)} days ago`, color: 'error', severity: 'error' };
  } else if (daysUntilExpiry <= 30) {
    return { status: `Expires in ${daysUntilExpiry} days`, color: 'warning', severity: 'warning' };
  } else if (daysUntilExpiry <= 90) {
    return { status: `Expires in ${daysUntilExpiry} days`, color: 'info', severity: 'info' };
  } else {
    return { status: `Expires in ${daysUntilExpiry} days`, color: 'success', severity: 'success' };
  }
};

// Get document status chip
const getDocumentStatusChip = (document) => {
  if (!document.isActive) {
    return <Chip label="Inactive" color="default" size="small" />;
  }
  
  if (!document.isCurrent) {
    return <Chip label="Superseded" color="warning" size="small" />;
  }
  
  const expStatus = getExpirationStatus(document.expiryDate);
  return <Chip label="Current" color="success" size="small" />;
};

function EmployeeDocumentProfile() {
  const { uid } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [groupedDocuments, setGroupedDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Upload dialog state
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    files: [],
    documentType: '',
    expiryDate: '',
    description: '',
    replaceExisting: false
  });
  const [uploadErrors, setUploadErrors] = useState({});
  
  // File input ref
  const fileInputRef = useRef(null);

  // Fetch grouped employee documents
  const fetchGroupedDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE_V1}/employee-documents/${uid}/grouped?includeInactive=true`, {
        headers: addAuthHeader()
      });
      
      setGroupedDocuments(response.data);
      
      // Auto-expand all groups to show document versions by default
      const autoExpand = {};
      response.data.groups?.forEach(group => {
        autoExpand[group.common?.documentType || group.documentType] = true;
      });
      setExpandedGroups(autoExpand);
      
    } catch (err) {
      console.error('Failed to fetch grouped documents:', err);
      setError('Failed to load employee documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uid) {
      fetchGroupedDocuments();
    }
  }, [uid]);

  // Handle group expansion
  const handleGroupToggle = (documentType) => {
    setExpandedGroups(prev => ({
      ...prev,
      [documentType]: !prev[documentType]
    }));
  };

  // Handle file upload
  const handleUpload = async () => {
    try {
      setUploading(true);
      setUploadErrors({});
      
      if (!uploadData.files.length) {
        setUploadErrors({ files: 'Please select at least one file' });
        return;
      }
      
      if (!uploadData.documentType) {
        setUploadErrors({ documentType: 'Please select a document type' });
        return;
      }

      // Prepare documents for bulk upload
      const documents = await Promise.all(uploadData.files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'USER_DOCUMENT');
        formData.append('documentType', uploadData.documentType);
        formData.append('employeeUid', uid);
        formData.append('fileName', file.name);
        
        if (uploadData.expiryDate) {
          formData.append('expiryDate', uploadData.expiryDate);
        }
        
        if (uploadData.description) {
          formData.append('description', uploadData.description);
        }
        
        // Upload individual file first
        const uploadResponse = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
          headers: {
            ...addAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Return document data for bulk API
        return {
          documentType: uploadData.documentType,
          fileName: file.name,
          filePath: uploadResponse.data.filePath,
          contentType: file.type,
          fileSize: file.size,
          expiryDate: uploadData.expiryDate || null,
          description: uploadData.description || null
        };
      }));
      
      // Use bulk document upload API
      await axios.post(`${API_BASE_V1}/employee-documents/bulk/${uid}`, {
        documents: documents
      }, {
        headers: addAuthHeader()
      });
      
      setSuccess(`Successfully uploaded ${uploadData.files.length} document(s)`);
      setOpenUploadDialog(false);
      setUploadData({
        files: [],
        documentType: '',
        expiryDate: '',
        description: '',
        replaceExisting: false
      });
      
      // Refresh documents
      await fetchGroupedDocuments();
      
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Failed to upload documents. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    setUploadData(prev => ({ ...prev, files }));
    setUploadErrors(prev => ({ ...prev, files: null }));
  };

  // Handle document download
  const handleDownload = async (document) => {
    try {
      let downloadUrl;
      
      // Use filePath if available (from grouped API), otherwise construct URL
      if (document.filePath) {
        const token = getAuthToken();
        downloadUrl = `${API_BASE_V1}/files/download?filePath=${encodeURIComponent(document.filePath)}&auth_token=${encodeURIComponent(token)}`;
      } else if (document.id) {
        downloadUrl = `${API_BASE_V1}/users/${uid}/documents/${document.id}/download`;
      } else {
        downloadUrl = `${API_BASE_V1}/users/${uid}/documents/${document.documentType}/download`;
      }
      
      const response = await axios.get(downloadUrl, {
        headers: addAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = document.fileName || `${document.documentType}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download document. Please try again.');
    }
  };

  // Handle document view
  const handleView = (document) => {
    let viewUrl;
    
    // Use filePath if available (from grouped API), otherwise construct URL
    if (document.filePath) {
      const token = getAuthToken();
      viewUrl = `${API_BASE_V1}/files/download?filePath=${encodeURIComponent(document.filePath)}&auth_token=${encodeURIComponent(token)}`;
    } else if (document.id) {
      viewUrl = `${API_BASE_V1}/users/${uid}/documents/${document.id}/download`;
    } else {
      viewUrl = `${API_BASE_V1}/users/${uid}/documents/${document.documentType}/download`;
    }
    
    window.open(viewUrl, '_blank');
  };

  // Render document summary cards
  const renderDocumentSummary = () => {
    if (!groupedDocuments?.groups) return null;
    
    // Calculate summary from groups data
    const groups = groupedDocuments.groups || [];
    const summary = groupedDocuments.summary || {};
    
    // Use API summary if available, otherwise calculate
    const totalDocuments = summary.totalDocuments || groups.reduce((sum, group) => sum + (group.documents?.length || 0), 0);
    const totalDocumentTypes = summary.documentTypes?.length || groups.length;
    
    // Calculate expiring and expired documents
    let expiringDocuments = 0;
    let expiredDocuments = 0;
    
    groups.forEach(group => {
      group.documents?.forEach(doc => {
        const expiryDate = doc.expiryDate || group.common?.expiryDate;
        if (expiryDate) {
          const daysUntilExpiry = calculateDaysUntilExpiry(expiryDate);
          if (daysUntilExpiry < 0) {
            expiredDocuments++;
          } else if (daysUntilExpiry <= 30) {
            expiringDocuments++;
          }
        }
      });
    });
    
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: 90,
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.dark',
                boxShadow: 2
              }
            }}
          >
            <CardContent sx={{ py: 2, px: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                <FilePresent color="primary" sx={{ fontSize: 18, mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  Total Documents
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'primary.main', textAlign: 'center' }}>
                {totalDocuments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: 90,
              border: '1px solid',
              borderColor: 'success.main',
              borderRadius: 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'success.dark',
                boxShadow: 2
              }
            }}
          >
            <CardContent sx={{ py: 2, px: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                <Description color="success" sx={{ fontSize: 18, mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  Document Types
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'success.main', textAlign: 'center' }}>
                {totalDocumentTypes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: 90,
              border: '1px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'warning.dark',
                boxShadow: 2
              }
            }}
          >
            <CardContent sx={{ py: 2, px: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                <Warning color="warning" sx={{ fontSize: 18, mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  Expiring Soon
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'warning.main', textAlign: 'center' }}>
                {expiringDocuments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: 90,
              border: '1px solid',
              borderColor: 'error.main',
              borderRadius: 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'error.dark',
                boxShadow: 2
              }
            }}
          >
            <CardContent sx={{ py: 2, px: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                <Error color="error" sx={{ fontSize: 18, mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  Expired
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'error.main', textAlign: 'center' }}>
                {expiredDocuments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render document group
  const renderDocumentGroup = (group) => {
    const documentType = group.common?.documentType || group.documentType;
    const documents = group.documents || [];
    const currentDocument = documents.find(doc => doc.isPrimary || doc.isCurrent);
    const hasExpiring = documents.some(doc => {
      const expiryDate = doc.expiryDate || group.common?.expiryDate;
      const daysUntilExpiry = calculateDaysUntilExpiry(expiryDate);
      return daysUntilExpiry !== null && daysUntilExpiry <= 30;
    });
    
    return (
      <Accordion
        key={group.documentGroupId || documentType}
        expanded={expandedGroups[documentType] || false}
        onChange={() => handleGroupToggle(documentType)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" width="100%">
            <Box flexGrow={1}>
              <Typography variant="h6">
                {documentType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Document'}
                {group.common?.title ? ` — ${group.common.title}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {documents.length} version{documents.length !== 1 ? 's' : ''}
                {currentDocument && (
                  <> • Current: Available</>
                )}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              {hasExpiring && (
                <Badge badgeContent="!" color="warning">
                  <Warning color="warning" />
                </Badge>
              )}
              
              {group.common?.isCurrent && (
                <Chip label="Current" color="success" size="small" />
              )}
              
              <Chip 
                label={`${documents.length} versions`} 
                size="small" 
                variant="outlined" 
              />
            </Box>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>File Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Upload Date</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents
                  .sort((a, b) => (b.version || 0) - (a.version || 0))
                  .map((document, index) => {
                    const expiryDate = document.expiryDate || group.common?.expiryDate;
                    const expStatus = getExpirationStatus(expiryDate);
                    
                    return (
                      <TableRow key={document.id || index} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2">
                              v{document.version || (documents.length - index)}
                            </Typography>
                            {(document.isPrimary || document.isCurrent) && (
                              <Chip 
                                label="Current" 
                                color="success" 
                                size="small" 
                                sx={{ ml: 1 }} 
                              />
                            )}
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {document.fileName || 'Document File'}
                          </Typography>
                          {document.description && (
                            <Typography variant="caption" color="text.secondary">
                              {document.description}
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(document.fileSize)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(document.createdAt || group.common?.issueDate)}
                          </Typography>
                          {document.createdBy && (
                            <Typography variant="caption" color="text.secondary">
                              by {document.createdBy}
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          {expiryDate ? (
                            <Chip
                              label={expStatus.status}
                              color={expStatus.color}
                              size="small"
                              icon={expStatus.severity === 'error' ? <Error /> : <Schedule />}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No expiry
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={document.active ? "Active" : "Inactive"} 
                            color={document.active ? "success" : "default"} 
                            size="small" 
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="View Document">
                              <IconButton
                                size="small"
                                onClick={() => handleView(document)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Download Document">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(document)}
                              >
                                <CloudDownload />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchGroupedDocuments}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/CST-Employees/list')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              Document Profile
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Employee UID: {uid}
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchGroupedDocuments}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenUploadDialog(true)}
          >
            Upload Document
          </Button>
        </Box>
      </Box>

      {/* Document Summary */}
      {renderDocumentSummary()}

      {/* Document Groups */}
      {groupedDocuments?.groups?.length > 0 ? (
        <Box>
          <Typography variant="h5" gutterBottom>
            Document Groups
          </Typography>
          {groupedDocuments.groups.map(renderDocumentGroup)}
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Documents Found
          </Typography>
          <Typography color="text.secondary" paragraph>
            This employee doesn't have any documents yet.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenUploadDialog(true)}
          >
            Upload First Document
          </Button>
        </Paper>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload New Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ height: 100, borderStyle: 'dashed' }}
                  startIcon={<CloudUpload />}
                >
                  {uploadData.files.length > 0 
                    ? `${uploadData.files.length} file(s) selected`
                    : 'Click to select files'
                  }
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFileSelect}
                    ref={fileInputRef}
                  />
                </Button>
                {uploadErrors.files && (
                  <Typography color="error" variant="caption">
                    {uploadErrors.files}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!uploadErrors.documentType}>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={uploadData.documentType}
                    label="Document Type"
                    onChange={(e) => setUploadData(prev => ({ 
                      ...prev, 
                      documentType: e.target.value 
                    }))}
                  >
                    {DOCUMENT_CATEGORIES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {uploadErrors.documentType && (
                    <Typography color="error" variant="caption">
                      {uploadErrors.documentType}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  type="date"
                  value={uploadData.expiryDate}
                  onChange={(e) => setUploadData(prev => ({ 
                    ...prev, 
                    expiryDate: e.target.value 
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  multiline
                  rows={3}
                  value={uploadData.description}
                  onChange={(e) => setUploadData(prev => ({ 
                    ...prev, 
                    description: e.target.value 
                  }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default EmployeeDocumentProfile;
